import { Injectable, EventEmitter } from '@angular/core';
import { Configuration } from '../models/configuration.model';
import { Purpose } from '../enums/purpose';
import { PriceService } from './price.service';
import { ThreeDService } from './three-d.service';
import { ApiService } from './api.service';
import { debounceTime, filter } from 'rxjs/operators';
import { FloorType } from '../models/floorType.model';
import { FloorTypes } from '../enums/floorTypes.enum';
import { StartupService } from './startup.service';
import { Settings } from '../models/settings.model';
import { ErrorService } from './error.service';
import { ErrorTypes } from '../enums/errorTypes.enum';
import { TranslateService } from '@ngx-translate/core';
import { NotificationService } from './notification.service';
import { DoorPosition } from '../models/doorPosition.model';
import { FinishTypes } from '../enums/finishTypes.enum';
import { WallSide } from '../enums/wallSide.enum';
import { ViewboxOptionWithPrice } from '../models/wallFinishType.model';
import { CompletionType } from '../enums/CompletionType.enum';
import { CompletionService } from './completion.service';
import { InnerWallType } from '../enums/innerWallType.enum';
import { DoorHandleType } from '../enums/doorHandleType.enum';
import { HingeType } from '../enums/hingeType.enum';
import { ConfigurationReceived } from '../models/configurationReceived.model';
import { Observable } from 'rxjs';
import { ProductClassificationService } from './product-classification.service';
import { ProductClass } from '../enums/productClass.enum';

const stepSizeLengthWidth = 100;
@Injectable({
  providedIn: 'root',
})
export class ConfigurationService {
  public static readonly COOLING_CELL_TIMER_LOAD_CONFIGURATION = 50;

  private _configuration: Configuration;
  private _minimumFreezingThickness = 80;
  private _defaultWallThicknessFreezing = 140;
  private _defaultWallThicknessCooling = 80;
  private _wallThicknesses = [60, 80, 100, 140];
  private _heights = [1800, 2100, 2400, 2700, 3000, 3300, 3600, 3900];
  private _furthestStepVisited = 1;
  private _wallsHidden = false;
  private productClassificationReceived = false;

  shoppingCartIdReady$: EventEmitter<Configuration> = new EventEmitter<Configuration>();
  configurationChanged$: EventEmitter<Configuration> = new EventEmitter<Configuration>();
  configurationSaved$: EventEmitter<Configuration> = new EventEmitter<Configuration>();
  updatePrice$: EventEmitter<Configuration> = new EventEmitter<Configuration>();
  hideFrontWallsChanged$: EventEmitter<boolean> = new EventEmitter<boolean>();
  updateBlockingValues$: EventEmitter<any> = new EventEmitter<any>();
  furthestStepVisitedUpdated$: EventEmitter<number> = new EventEmitter<number>();
  productClassChanged$: EventEmitter<ProductClass> = new EventEmitter<ProductClass>();
  saveConfigurationEndpoint = 'api/configuration/save';
  generate2dPlanEndpoint = 'api/configuration/generate2DPlan';
  getConfigurationEndpoint = 'api/configuration/';

  externalWallFinishViewboxOptions: Record<FinishTypes, ViewboxOptionWithPrice<FinishTypes>> = {
    [FinishTypes.STEEL]: new ViewboxOptionWithPrice(
      true,
      'FINISHTYPESFULL.STEEL',
      '/assets/images/white9010.jpg',
      FinishTypes.STEEL,
      ''
    ),
    [FinishTypes.STEEL_PET55]: new ViewboxOptionWithPrice(
      false,
      'FINISHTYPESFULL.STEELPET55',
      '/assets/images/white9010PET.jpg',
      FinishTypes.STEEL_PET55,
      ''
    ),
    [FinishTypes.RVS304_COLLED_ROLLED]: new ViewboxOptionWithPrice(
      false,
      'FINISHTYPESFULL.RVS304COLLEDROLLED',
      '/assets/images/metal.jpg',
      FinishTypes.RVS304_COLLED_ROLLED,
      ''
    ),
    [FinishTypes.RVS304_DESIGN_5WL]: new ViewboxOptionWithPrice(
      false,
      'FINISHTYPESFULL.RVS304DESIGN5WL',
      '/assets/images/5WL.jpg',
      FinishTypes.RVS304_DESIGN_5WL,
      '',
      true
    ),
    [FinishTypes.RVS304_LINEN]: new ViewboxOptionWithPrice(
      false,
      'FINISHTYPESFULL.RVS304LINEN',
      '/assets/images/linen.jpg',
      FinishTypes.RVS304_LINEN,
      '',
      true
    ),
    [FinishTypes.RVS304_LINE_BRUSH]: new ViewboxOptionWithPrice(
      false,
      'FINISHTYPESFULL.RVS304LINEBRUSH',
      '/assets/images/metal_turned.jpg',
      FinishTypes.RVS304_LINE_BRUSH,
      ''
    ),
    [FinishTypes.RVS304_ROUND_BRUSH]: new ViewboxOptionWithPrice(
      false,
      'FINISHTYPESFULL.RVS304ROUNDBRUSH',
      '/assets/images/circular.jpg',
      FinishTypes.RVS304_ROUND_BRUSH,
      ''
    ),
    [FinishTypes.RVS316_ROUND_BRUSH]: new ViewboxOptionWithPrice(
      false,
      'FINISHTYPESFULL.RVS316ROUNDBRUSH',
      '/assets/images/circular.jpg',
      FinishTypes.RVS316_ROUND_BRUSH,
      '',
      true
    ),
  };

  internalWallFinishViewboxOptions: Record<FinishTypes, ViewboxOptionWithPrice<FinishTypes>> = {
    [FinishTypes.STEEL]: new ViewboxOptionWithPrice(
      true,
      'FINISHTYPESFULL.STEEL',
      '/assets/images/white9010.jpg',
      FinishTypes.STEEL,
      ''
    ),
    [FinishTypes.STEEL_PET55]: new ViewboxOptionWithPrice(
      false,
      'FINISHTYPESFULL.STEELPET55',
      '/assets/images/white9010PET.jpg',
      FinishTypes.STEEL_PET55,
      ''
    ),
    [FinishTypes.RVS304_COLLED_ROLLED]: new ViewboxOptionWithPrice(
      false,
      'FINISHTYPESFULL.RVS304COLLEDROLLED',
      '/assets/images/metal.jpg',
      FinishTypes.RVS304_COLLED_ROLLED,
      ''
    ),
    [FinishTypes.RVS304_DESIGN_5WL]: new ViewboxOptionWithPrice(
      false,
      'FINISHTYPESFULL.RVS304DESIGN5WL',
      '/assets/images/5WL.jpg',
      FinishTypes.RVS304_DESIGN_5WL,
      '',
      true
    ),
    [FinishTypes.RVS304_LINEN]: new ViewboxOptionWithPrice(
      false,
      'FINISHTYPESFULL.RVS304LINEN',
      '/assets/images/linen.jpg',
      FinishTypes.RVS304_LINEN,
      '',
      true
    ),
    [FinishTypes.RVS304_LINE_BRUSH]: new ViewboxOptionWithPrice(
      false,
      'FINISHTYPESFULL.RVS304LINEBRUSH',
      '/assets/images/metal_turned.jpg',
      FinishTypes.RVS304_LINE_BRUSH,
      ''
    ),
    [FinishTypes.RVS304_ROUND_BRUSH]: new ViewboxOptionWithPrice(
      false,
      'FINISHTYPESFULL.RVS304ROUNDBRUSH',
      '/assets/images/circular.jpg',
      FinishTypes.RVS304_ROUND_BRUSH,
      ''
    ),
    [FinishTypes.RVS316_ROUND_BRUSH]: new ViewboxOptionWithPrice(
      false,
      'FINISHTYPESFULL.RVS316ROUNDBRUSH',
      '/assets/images/circular.jpg',
      FinishTypes.RVS316_ROUND_BRUSH,
      '',
      true
    ),
  };

  externalDoorFinishViewboxOptions: Record<FinishTypes, ViewboxOptionWithPrice<FinishTypes>> = {
    [FinishTypes.STEEL]: new ViewboxOptionWithPrice(
      true,
      'FINISHTYPESFULL.STEEL',
      '/assets/images/white9010.jpg',
      FinishTypes.STEEL,
      ''
    ),
    [FinishTypes.STEEL_PET55]: new ViewboxOptionWithPrice(
      false,
      'FINISHTYPESFULL.STEELPET55',
      '/assets/images/white9010PET.jpg',
      FinishTypes.STEEL_PET55,
      ''
    ),
    [FinishTypes.RVS304_COLLED_ROLLED]: new ViewboxOptionWithPrice(
      false,
      'FINISHTYPESFULL.RVS304COLLEDROLLED',
      '/assets/images/metal.jpg',
      FinishTypes.RVS304_COLLED_ROLLED,
      ''
    ),
    [FinishTypes.RVS304_DESIGN_5WL]: new ViewboxOptionWithPrice(
      false,
      'FINISHTYPESFULL.RVS304DESIGN5WL',
      '/assets/images/5WL.jpg',
      FinishTypes.RVS304_DESIGN_5WL,
      '',
      true
    ),
    [FinishTypes.RVS304_LINEN]: new ViewboxOptionWithPrice(
      false,
      'FINISHTYPESFULL.RVS304LINEN',
      '/assets/images/linen.jpg',
      FinishTypes.RVS304_LINEN,
      '',
      true
    ),
    [FinishTypes.RVS304_LINE_BRUSH]: new ViewboxOptionWithPrice(
      false,
      'FINISHTYPESFULL.RVS304LINEBRUSH',
      '/assets/images/metal_turned.jpg',
      FinishTypes.RVS304_LINE_BRUSH,
      ''
    ),
    [FinishTypes.RVS304_ROUND_BRUSH]: new ViewboxOptionWithPrice(
      false,
      'FINISHTYPESFULL.RVS304ROUNDBRUSH',
      '/assets/images/circular.jpg',
      FinishTypes.RVS304_ROUND_BRUSH,
      ''
    ),
    [FinishTypes.RVS316_ROUND_BRUSH]: new ViewboxOptionWithPrice(
      false,
      'FINISHTYPESFULL.RVS316ROUNDBRUSH',
      '/assets/images/circular.jpg',
      FinishTypes.RVS316_ROUND_BRUSH,
      '',
      true
    ),
  };

  internalDoorFinishViewboxOptions: Record<FinishTypes, ViewboxOptionWithPrice<FinishTypes>> = {
    [FinishTypes.STEEL]: new ViewboxOptionWithPrice(
      true,
      'FINISHTYPESFULL.STEEL',
      '/assets/images/white9010.jpg',
      FinishTypes.STEEL,
      ''
    ),
    [FinishTypes.STEEL_PET55]: new ViewboxOptionWithPrice(
      false,
      'FINISHTYPESFULL.STEELPET55',
      '/assets/images/white9010PET.jpg',
      FinishTypes.STEEL_PET55,
      ''
    ),
    [FinishTypes.RVS304_COLLED_ROLLED]: new ViewboxOptionWithPrice(
      false,
      'FINISHTYPESFULL.RVS304COLLEDROLLED',
      '/assets/images/metal.jpg',
      FinishTypes.RVS304_COLLED_ROLLED,
      ''
    ),
    [FinishTypes.RVS304_DESIGN_5WL]: new ViewboxOptionWithPrice(
      false,
      'FINISHTYPESFULL.RVS304DESIGN5WL',
      '/assets/images/5WL.jpg',
      FinishTypes.RVS304_DESIGN_5WL,
      '',
      true
    ),
    [FinishTypes.RVS304_LINEN]: new ViewboxOptionWithPrice(
      false,
      'FINISHTYPESFULL.RVS304LINEN',
      '/assets/images/linen.jpg',
      FinishTypes.RVS304_LINEN,
      '',
      true
    ),
    [FinishTypes.RVS304_LINE_BRUSH]: new ViewboxOptionWithPrice(
      false,
      'FINISHTYPESFULL.RVS304LINEBRUSH',
      '/assets/images/metal_turned.jpg',
      FinishTypes.RVS304_LINE_BRUSH,
      ''
    ),
    [FinishTypes.RVS304_ROUND_BRUSH]: new ViewboxOptionWithPrice(
      false,
      'FINISHTYPESFULL.RVS304ROUNDBRUSH',
      '/assets/images/circular.jpg',
      FinishTypes.RVS304_ROUND_BRUSH,
      ''
    ),
    [FinishTypes.RVS316_ROUND_BRUSH]: new ViewboxOptionWithPrice(
      false,
      'FINISHTYPESFULL.RVS316ROUNDBRUSH',
      '/assets/images/circular.jpg',
      FinishTypes.RVS316_ROUND_BRUSH,
      '',
      true
    ),
  };
  doorHandleTypes: Record<DoorHandleType, ViewboxOptionWithPrice<DoorHandleType> | null> = {
    [DoorHandleType.euro2015]: new ViewboxOptionWithPrice(
      true,
      'DOORHANDLE.EURO2015',
      '/assets/images/doorHandles/euro2015.png',
      DoorHandleType.euro2015,
      ''
    ),
    [DoorHandleType.euro2000]: new ViewboxOptionWithPrice(
      false,
      'DOORHANDLE.EURO2000',
      '/assets/images/doorHandles/euro2000.png',
      DoorHandleType.euro2000,
      ''
    ),
    [DoorHandleType.euro2016]: new ViewboxOptionWithPrice(
      false,
      'DOORHANDLE.EURO2016',
      '/assets/images/doorHandles/euro2016.png',
      DoorHandleType.euro2016,
      ''
    ),
    [DoorHandleType.null]: new ViewboxOptionWithPrice(
      false,
      'DOORHANDLE.NONE',
      '/assets/images/doorHandles/geen.png',
      DoorHandleType.null,
      ''
    ),
  };
  doorHingeTypes: Record<HingeType, ViewboxOptionWithPrice<HingeType>> = {
    [HingeType.normal]: new ViewboxOptionWithPrice(
      true,
      'DOORHINGE.NORMAL',
      '/assets/images/doorHinges/normal.png',
      HingeType.normal,
      ''
    ),
    [HingeType.normalNotRising]: new ViewboxOptionWithPrice(
      false,
      'DOORHINGE.NORMALNOTRISING',
      '/assets/images/doorHinges/normalnotrising.png',
      HingeType.normalNotRising,
      ''
    ),
    [HingeType.chrome]: new ViewboxOptionWithPrice(
      false,
      'DOORHINGE.CHROME',
      '/assets/images/doorHinges/chrome.png',
      HingeType.chrome,
      ''
    ),
  };
  private _allowedFloorTypes: Record<FloorTypes, FloorType> = {
    [FloorTypes.NONE]: {
      available: true,
      option: FloorTypes.NONE,
      strong: null,
      minimumWallThickness: 0,
      description: 'FLOORTYPES.NONE',
      selected: false,
      content: '',
    },
    [FloorTypes.ANTISLIP]: {
      available: false,
      option: FloorTypes.ANTISLIP,
      strong: false,
      minimumWallThickness: 0,
      description: 'FLOORTYPES.ANTISLIP',
      selected: true,
      content: `/assets/images/floors/antislip.png`,
      link: 'https://downloads.isomasters.com/technical/Tech%20details%20floorpanel%20-%20Standard%209mm.pdf',
    },
    [FloorTypes.DURAFLOOR]: {
      available: true,
      option: FloorTypes.DURAFLOOR,
      strong: false,
      minimumWallThickness: 0,
      description: 'FLOORTYPES.DURAFLOOR',
      selected: true,
      content: `/assets/images/floors/DuraFloor.png`,
      link: 'https://downloads.isomasters.com/technical/Tech%20details%20floorpanel%20-%20Standard%209mm.pdf',
    },
    [FloorTypes.REINFORCEDANTISLIP]: {
      available: false,
      option: FloorTypes.REINFORCEDANTISLIP,
      strong: false,
      minimumWallThickness: 80,
      description: 'FLOORTYPES.REINFORCEDANTISLIP',
      selected: false,
      content: `/assets/images/floors/Versterkte.png`,
      link: 'https://downloads.isomasters.com/technical/Tech%20details%20floorpanel%20-%20Reinforced%20standard%2024mm.pdf',
    },
    [FloorTypes.ALUMINUM25]: {
      available: true,
      option: FloorTypes.ALUMINUM25,
      strong: true,
      minimumWallThickness: 0,
      description: 'FLOORTYPES.ALUMINUM25',
      selected: false,
      content: `/assets/images/floors/Aluminium traanplaat.png`,
    },
    [FloorTypes.ALUMINUM35]: {
      available: true,
      option: FloorTypes.ALUMINUM35,
      strong: true,
      minimumWallThickness: 0,
      description: 'FLOORTYPES.ALUMINUM35',
      selected: false,
      content: `/assets/images/floors/Aluminium traanplaat.png`,
    },
    [FloorTypes.STEELSMOOTH3]: {
      available: true,
      option: FloorTypes.STEELSMOOTH3,
      strong: true,
      minimumWallThickness: 0,
      description: 'FLOORTYPES.STEELSMOOTH3',
      selected: false,
      content: `/assets/images/floors/Galva staalplaat.png`,
    },
    [FloorTypes.STEELANTISLIP5]: {
      available: true,
      option: FloorTypes.STEELANTISLIP5,
      strong: true,
      minimumWallThickness: 0,
      description: 'FLOORTYPES.STEELANTISLIP5',
      selected: false,
      content: `/assets/images/floors/Staalplaat eenzijdig gemetalliseerd.png`,
    },
    [FloorTypes.RVSANTISLIPR13]: {
      available: true,
      option: FloorTypes.RVSANTISLIPR13,
      strong: false,
      minimumWallThickness: 0,
      description: 'FLOORTYPES.RVSANTISLIPR13',
      selected: false,
      content: `/assets/images/floors/RVS plaat 5WL.png`,
      link: 'https://downloads.isomasters.com/technical/Tech%20details%20floorpanel%20-%20Stainless%20steel%200.6mm.pdf',
    },
  };
  registerNewDoors = false;
  doorsAreBeingSet: boolean;
  configSavedSuccessfully: boolean;

  defaultTurnDoorWidth = 620;
  defaultTurnDoorheight = 1985;
  defaultTurnDoorheightUnder2m = 1800;
  defaultSlideDoorWidth = 800;
  defaultSlideDoorheight = 1900;
  readonly roundedWallCorrectionThickness = 15;

  constructor(
    private priceService: PriceService,
    private threeDService: ThreeDService,
    private apiService: ApiService,
    private startUpService: StartupService,
    private errorService: ErrorService,
    private translateService: TranslateService,
    private notificationService: NotificationService,
    private completionService: CompletionService,
    private productClassificationService: ProductClassificationService
  ) {
    this.completionService.terminated$.subscribe(() => {
      this.configuration.completionType = CompletionType.Cancelled;
      this.saveConfiguration(this.configuration);
    });

    this.threeDService.readyReceived$.subscribe(() => {
      setTimeout(() => {
        this.reset3dModel();
      });
    });
    this.threeDService.doorAdded$.pipe(filter(() => this.registerNewDoors)).subscribe(() => {
      this.updateDoorSettings();
    });

    this.threeDService.doorDroppedOffWall$.subscribe(() => {
      this.updateDoorSettings();
    });

    this.threeDService.doorRemoved$.subscribe(() => {
      this.updateDoorSettings();
    });

    this.threeDService.updateDoors$.subscribe(() => {
      this.updateDoorSettings();
    });

    this.startUpService.priceEnabledSetting$.subscribe((pricesEnabled: boolean) => {
      this.priceService.priceEnabled = pricesEnabled;
    });

    this.startUpService.configurationReceived$.subscribe(async (data: ConfigurationReceived) => {
      try {
        const config = await this.getConfiguration(data.configurationId);
        if (!config) {
          this.errorService.throwError(ErrorTypes.ConfigurationNotFound);
        }
        let coolingCellTimer: number = undefined;
        if (data.makeCopy) {
          config.configurationId = undefined;
          coolingCellTimer = ConfigurationService.COOLING_CELL_TIMER_LOAD_CONFIGURATION;
        }
        this.handleExistingConfiguration(config, data.languageOverride, coolingCellTimer);
      } catch (e) {
        if (e.status === 404) {
          this.errorService.throwError(ErrorTypes.ConfigurationNotFound);
          return;
        }
        this.errorService.throwError(ErrorTypes.Generic, (e as Error).message);
      }
    });

    this.startUpService.sharedLinkConfiguration$.subscribe(async (data: false | string) => {
      if (data === false) {
        this._configuration = new Configuration();
        this.startUpService.initialized$.next(true);
        return;
      }
      this.priceService.priceEnabled = false;
      try {
        this.notificationService.showNotification('NOTIFICATIONS.READONLYLINK', NotificationService.DEFAULT_TIMEOUT);
        const config = await this.getConfiguration(data);
        if (!config) {
          this.errorService.throwError(ErrorTypes.ConfigurationNotFound);
        }
        this.handleExistingConfiguration(config);
      } catch (e) {
        if (e.status === 404) {
          this.errorService.throwError(ErrorTypes.ConfigurationNotFound);
          return;
        }
        this.errorService.throwError(ErrorTypes.Generic, (e as Error).message);
      }
    });

    this.configurationChanged$.pipe(debounceTime(1000)).subscribe(async (configuration: Configuration) => {
      this.saveConfiguration(configuration);
    });

    this.priceService.pricesLoaded$.subscribe(() => {
      if (!this.configSavedSuccessfully) {
        this.configurationChanged$.emit(this._configuration);
      }
    });

    this.updatePrice$.pipe(debounceTime(50)).subscribe(async (configuration: Configuration) => {
      this.priceService.updatePrice(configuration);
    });

    productClassificationService.classificationReceived$.subscribe(() => {
      this.productClassificationReceived = true;
      const productClass = this.productClassificationService.GetCellProductClass(this._configuration);
      this.productClass = productClass;
    });
  }

  handleExistingConfiguration(config: Configuration, languageOverride?: string, coolingCellTimer?: number) {
    this.translateService.use(config.language);
    this._configuration.configurationId = config.configurationId;
    this._configuration.language = languageOverride || config.language;
    this._configuration.settings = this.useValuesOrDefault(config.settings);
    if (this.productClassificationReceived) {
      const productClass = this.productClassificationService.GetCellProductClass(this._configuration);
      this.productClass = productClass;
    }
    this.reset3dModel(coolingCellTimer);
    this.threeDService.resetDefaultCamera(true);
    this.startUpService.initialized$.next(true);
  }

  private useValuesOrDefault(config: Settings): Settings {
    const settings = new Settings();
    settings.floorType = config.floorType || settings.floorType;
    settings.heightInMM = config.heightInMM || settings.heightInMM;
    settings.lengthInMM = config.lengthInMM || settings.lengthInMM;
    settings.widthInMM = config.widthInMM || settings.widthInMM;
    settings.wallThickness = config.wallThickness || settings.wallThickness;
    if (!this._wallThicknesses.includes(settings.wallThickness)) {
      if (settings.wallThickness > Math.max(...this._wallThicknesses)) {
        settings.wallThickness = Math.max(...this._wallThicknesses);
      } else {
        settings.wallThickness = this._wallThicknesses.filter(a => a >= settings.wallThickness)[0];
      }
    }
    settings.purpose = config.purpose || settings.purpose;
    settings.doorSettings.splice(0, settings.doorSettings.length);

    (config._doorSettings || settings._doorSettings).forEach(ds => {
      const doorSetting = new DoorPosition(
        ds.id,
        ds.left,
        ds.side,
        ds.doorModel.openingWidth,
        ds.doorModel.openingHeight,
        ds.doorModel.panelWidth,
        ds.doorModel.panelHeight,
        ds.frameInteriorFinish,
        ds.frameExteriorFinish,
        ds.leafInteriorFinish,
        ds.leafExteriorFinish,
        ds.doorModel.type,
        ds.doorModel.direction,
        ds.hingeType,
        ds.doorHandleType,
        ds.innerWallId
      );
      settings.doorSettings.push(doorSetting);
    });
    settings.plinthsInside = config.plinthsInside != null ? config.plinthsInside : settings.plinthsInside;
    settings.plinthsOutside = config.plinthsOutside != null ? config.plinthsOutside : settings.plinthsOutside;
    settings.ventilationProfiles =
      config.ventilationProfiles != null ? config.ventilationProfiles : settings.ventilationProfiles;
    settings.sunkenFloor = config.sunkenFloor != null ? config.sunkenFloor : settings.sunkenFloor;
    settings.ceilingMoldings = config.ceilingMoldings != null ? config.ceilingMoldings : settings.ceilingMoldings;

    settings.wallFinishes = config.wallFinishes != null ? config.wallFinishes : settings.wallFinishes;
    settings.innerWalls = config.innerWalls != null ? config.innerWalls : settings.innerWalls;
    return settings;
  }

  async saveAndGetConfigurationId() {
    await this.saveConfiguration(this._configuration, true);
  }

  generate2dPlan(configuration: Configuration) {
    configuration.language = this.translateService.currentLang;
    const configurationForBackend = this.removeExcessProperties(configuration);

    return this.apiService.post(this.generate2dPlanEndpoint, configurationForBackend, 'blob');
  }
  async saveConfiguration(configuration: Configuration, shoppingCartTime = false) {
    configuration.language = this.translateService.currentLang;
    if (this.priceService.priceEnabled && this.priceService.hasPrices) {
      configuration = await this.addPricesToConfiguration(configuration);
      if (!configuration) {
        this.configSavedSuccessfully = false;
        return;
      }
    }
    configuration = this.removeExcessProperties(configuration);
    if (shoppingCartTime) {
      this.configuration.completionType = CompletionType.Completed;
    }

    this.apiService.post(this.saveConfigurationEndpoint, configuration).subscribe(
      response => {
        this._configuration.configurationId = response.body.configurationId;
        if (shoppingCartTime) {
          this.shoppingCartIdReady$.emit(this._configuration);
        }
        this.configSavedSuccessfully = true;
        this.configurationSaved$.next(configuration);
      },
      err => {
        console.log(err);
        this.configSavedSuccessfully = false;

        if (err.error === 'Cannot save config in AX') {
          this.errorService.throwError(ErrorTypes.InvalidAxToken, err);
        } else {
          this.errorService.throwError(ErrorTypes.BackendNotFound, err);
        }
      }
    );

    this.configSavedSuccessfully = true;
  }

  async addPricesToConfiguration(configuration: Configuration): Promise<Configuration> {
    const totalPricesExcludingDoors = this.priceService.calculateTotalPriceExcludingDoors(configuration);
    const calculateDoorPrice = await this.priceService.getPricesOfDoors(configuration);
    if (totalPricesExcludingDoors === false || calculateDoorPrice === false) {
      return;
    }
    configuration.settings.salesPrice = totalPricesExcludingDoors;

    configuration.settings.salesTotal = totalPricesExcludingDoors + calculateDoorPrice;

    for (const door of configuration.settings.doorSettings) {
      const price = await this.priceService.getPriceOfDoor(
        door,
        configuration.settings.wallThickness,
        configuration.settings.purpose !== Purpose.Cooling
      );
      if (price === false) {
        return;
      }
      door.salesPrice = price.Price;
    }
    return configuration;
  }

  removeExcessProperties(configuration: Configuration): Configuration {
    let cleanConfig = new Configuration();
    cleanConfig = Object.assign(cleanConfig, configuration);

    if (cleanConfig.settings.doorSettings) {
      cleanConfig.settings.doorSettings.forEach(d => {
        d.selected = undefined;
        d.image = undefined;
        if (d.mesh) {
          d.mesh = undefined;
        }
      });
    }
    return cleanConfig;
  }

  async getConfiguration(configurationId: string): Promise<Configuration> {
    return this.apiService.get<Configuration>(this.getConfigurationEndpoint + configurationId).toPromise();
  }

  doesConfigurationExist(configurationId: string): Observable<boolean> {
    return this.apiService.get<boolean>(this.getConfigurationEndpoint + configurationId + '/exists');
  }

  checkForIncompatibleDoorHandles() {
    if (this.purpose === Purpose.Freezing && this.wallThickness < 100) {
      let update = false;
      this.configuration.settings.doorSettings
        .filter(d => d.doorHandleType === DoorHandleType.euro2000 || d.doorHandleType === DoorHandleType.euro2016)
        .forEach(door => {
          update = true;
          door.doorHandleType = DoorHandleType.euro2015;
          this.threeDService.changeDoorHandle(door, DoorHandleType.euro2015);
        });
      if (update) {
        this.updateDoorSettings();
      }
    }
  }
  checkForIncompatibleWalls() {
    if (this.wallThickness <= 80) {
      let update = false;
      [...this.configuration.settings.innerWalls].forEach(wall => {
        if (wall.type === InnerWallType.lProfile || this.wallThickness < 80) {
          update = true;
          this.threeDService.removeInnerWall(wall);
        }
      });
      if (update) {
        this.updateDoorSettings();
        let message = 'STEPINNERWALL.DELETED_INNER_WALLS_TYPE_INNER_WALL_NOT_ALLOWED_AT_THIS_THICKNESS';
        if (this.wallThickness < 80) {
          message = 'STEPINNERWALL.DELETED_INNER_WALLS_NO_INNER_WALL_AT_THIS_THICKNESS';
        }
        this.notificationService.showNotification(message, NotificationService.DEFAULT_TIMEOUT);
      }
    }
  }
  get mustHaveFloor(): boolean {
    if (this._configuration.settings.purpose !== Purpose.Cooling) {
      return true;
    }
    if (this.wallThickness > 100) {
      return true;
    }
    return false;
  }
  get configuration(): Configuration {
    return this._configuration;
  }

  get minimumFreezingThickness(): number {
    return this._minimumFreezingThickness;
  }
  get defaultWallThicknessFreezing(): number {
    return this._defaultWallThicknessFreezing;
  }
  get defaultWallThicknessCooling(): number {
    return this._defaultWallThicknessCooling;
  }

  get furthestStepVisited(): number {
    return this._furthestStepVisited;
  }

  set furthestStepVisited(step: number) {
    this._furthestStepVisited = Math.max(step, this._furthestStepVisited);
    this.furthestStepVisitedUpdated$.emit(this._furthestStepVisited);
  }

  get canUseInnerWalls(): boolean {
    return this.wallThickness > 60;
  }

  set purpose(value: Purpose) {
    this._configuration.settings.purpose = value;
    if (this._furthestStepVisited === 1) {
      if (value === Purpose.Cooling) {
        this.wallThickness = this._defaultWallThicknessCooling;
      } else {
        this.wallThickness = this._defaultWallThicknessFreezing;
      }
    } else {
      if (value !== Purpose.Cooling && this._configuration.settings.wallThickness < this._minimumFreezingThickness) {
        this.wallThickness = this._minimumFreezingThickness;
      }
    }

    if (this.mustHaveFloor && this.floorType === FloorTypes.NONE) {
      this.floorType = FloorTypes.DURAFLOOR;
    }
    this.checkForIncompatibleDoorHandles();
    this.checkForInvalidPlinths();
    this.configurationChanged$.emit(this._configuration);
    this.updatePrice$.emit(this._configuration);
  }

  get purpose(): Purpose {
    return this._configuration.settings.purpose;
  }

  set wallThickness(value: number) {
    if (!this._wallThicknesses.includes(value)) {
      if (value > Math.max(...this._wallThicknesses)) {
        value = Math.max(...this._wallThicknesses);
      } else {
        value = this._wallThicknesses.filter(a => a >= value)[0];
      }
    }
    this.threeDService.setWallThickness(value);
    this._configuration.settings.wallThickness = value;
    if (this.mustHaveFloor && this.floorType === FloorTypes.NONE) {
      this.floorType = FloorTypes.DURAFLOOR;
    }
    if (this.allowedFloorTypesRecord[this.floorType].minimumWallThickness > value) {
      this.floorType = FloorTypes.DURAFLOOR;
      this.notificationService.showNotification(
        this.translateService.instant('NOTIFICATIONS.ONLYSTANDARDFLOOR'),
        NotificationService.DEFAULT_TIMEOUT
      );
    }
    this.checkForIncompatibleDoorHandles();
    this.checkForIncompatibleWalls();
    this.checkForInvalidPlinths();
    this.configurationChanged$.emit(this._configuration);
    this.updatePrice$.emit(this._configuration);
  }

  get wallThickness(): number {
    return this._configuration.settings.wallThickness;
  }

  set length(value: number) {
    value = +value;
    if (value === this.configuration.settings.lengthInMM) {
      return;
    }

    if (value % stepSizeLengthWidth !== 0) {
      value = Math.ceil(value / stepSizeLengthWidth) * stepSizeLengthWidth;
    }
    if (value < 900) {
      value = 900;
    }
    if (value > 12000) {
      value = 12000;
    }
    this.threeDService.setLength(value / 1000);
    this.configuration.settings.lengthInMM = value;

    const innerWalls = this.threeDService.getInnerWalls();
    const innerWallsNoLongerFeasible = innerWalls.filter(
      iw =>
        (iw.placedOnSide == WallSide.north || iw.placedOnSide == WallSide.south) &&
        ((iw.left > value - 300 && iw.type == InnerWallType.threeway) ||
          (iw.left > value - 100 && iw.type == InnerWallType.lProfile))
    );

    innerWallsNoLongerFeasible.forEach(iw => {
      this.threeDService.removeInnerWall(iw);
    });
    if (innerWallsNoLongerFeasible.length > 0) {
      this.updateInnerWalls();
    }
    this.configurationChanged$.emit(this._configuration);
    this.updatePrice$.emit(this.configuration);
  }

  get length(): number {
    return this.configuration.settings.lengthInMM;
  }
  set width(value: number) {
    value = +value;
    if (value === this.configuration.settings.widthInMM) {
      return;
    }

    if (value % stepSizeLengthWidth !== 0) {
      value = Math.ceil(value / stepSizeLengthWidth) * stepSizeLengthWidth;
    }
    if (value < 900) {
      value = 900;
    }
    if (value > 12000) {
      value = 12000;
    }
    this.threeDService.setWidth(value / 1000);
    this.configuration.settings.widthInMM = value;

    const innerWalls = this.threeDService.getInnerWalls();
    const innerWallsNoLongerFeasible = innerWalls.filter(
      iw =>
        (iw.placedOnSide == WallSide.east || iw.placedOnSide == WallSide.west) &&
        ((iw.left > value - 300 && iw.type == InnerWallType.threeway) ||
          (iw.left > value - 100 && iw.type == InnerWallType.lProfile))
    );

    innerWallsNoLongerFeasible.forEach(iw => {
      this.threeDService.removeInnerWall(iw);
    });
    if (innerWallsNoLongerFeasible.length > 0) {
      this.updateInnerWalls();
    }

    this.configurationChanged$.emit(this._configuration);
    this.updatePrice$.emit(this.configuration);
  }

  get width(): number {
    return this.configuration.settings.widthInMM;
  }
  set height(value: number) {
    value = +value;

    if (value === this.configuration.settings.heightInMM) {
      return;
    }

    if (!this._heights.includes(value)) {
      value = this._heights.filter(a => a >= value)[0];
    }
    this.threeDService.setHeight(value / 1000);
    this.configuration.settings.heightInMM = value;
    if (value <= 1800) {
      this.threeDService.removeDoorsOnInnerWalls(true);
    }
    this.updateDoorSettings();
    this.configurationChanged$.emit(this._configuration);
    this.updatePrice$.emit(this.configuration);
  }

  get height(): number {
    return this.configuration.settings.heightInMM;
  }

  get allowedWallThicknesses(): number[] {
    if (this._configuration.settings.purpose === Purpose.Cooling) {
      return this._wallThicknesses;
    }
    return this._wallThicknesses.filter(v => v >= this._minimumFreezingThickness);
  }

  get wallsHidden(): boolean {
    return this._wallsHidden;
  }

  set floorType(value: FloorTypes) {
    if (this.configuration.settings.floorType === value) {
      return;
    }

    this.allowedFloorTypes.forEach(f => (f.selected = false));

    const floorTypeToUpdate = this.allowedFloorTypes.find(f => f.option === value);
    if (floorTypeToUpdate) {
      floorTypeToUpdate.selected = true;
    }

    this._configuration.settings.floorType = value;
    this.configurationChanged$.emit(this._configuration);
    this.updatePrice$.emit(this.configuration);
    this.setFloorTypeIn3D(value);
  }
  get floorType(): FloorTypes {
    return this.configuration.settings.floorType;
  }

  get productClass(): ProductClass {
    return this.configuration.settings.productClass;
  }

  set productClass(value: ProductClass) {
    const configuration = this._configuration;
    const cellProductClass = this.productClassificationService.GetCellProductClass(configuration);
    if (cellProductClass !== configuration.settings.productClass) {
      this._configuration.settings.productClass = cellProductClass;
      this.configurationChanged$.emit(this._configuration);
      this.productClassChanged$.emit(cellProductClass);
    }
  }

  private setFloorTypeIn3D(value: FloorTypes) {
    if (value === FloorTypes.NONE) {
      this.threeDService.setFloorVisible(false);
      return;
    }
    if (this.threeDService.getFloorVisible() === false) {
      this.threeDService.setFloorVisible(true);
    }
    const finish = this.floorTypeToMaterial(value);
    this.threeDService.setFloorMaterial(finish);
  }

  private floorTypeToMaterial(floorType: FloorTypes): string {
    switch (floorType as FloorTypes) {
      case FloorTypes.ALUMINUM25:
      case FloorTypes.ALUMINUM35:
        return 'rvs-diamondplate';
      case FloorTypes.RVSANTISLIPR13:
        return 'rvs-plate';
      case FloorTypes.STEELANTISLIP5:
        return 'rvs-steelplate';
      case FloorTypes.STEELSMOOTH3:
        return 'rvs';
      case FloorTypes.DURAFLOOR:
        return 'durafloor';
      case FloorTypes.ANTISLIP:
      case FloorTypes.REINFORCEDANTISLIP:
      default:
        return 'antislip';
    }
  }

  reset3dModel(coolingCellCounterOverride?: number) {
    const wallFinishes = [
      this._configuration.settings.wallFinishes[WallSide.north],
      this._configuration.settings.wallFinishes[WallSide.east],
      this._configuration.settings.wallFinishes[WallSide.south],
      this._configuration.settings.wallFinishes[WallSide.west],
    ];

    this.threeDService.recreateCoolingCell(
      Math.ceil((this._configuration.settings.lengthInMM / 1000) * 10) / 10,
      Math.ceil((this._configuration.settings.heightInMM / 1000) * 10) / 10,
      Math.ceil((this._configuration.settings.widthInMM / 1000) * 10) / 10,
      this._configuration.settings.wallThickness,
      this.floorTypeToMaterial(this._configuration.settings.floorType),
      this._configuration.settings.floorType !== FloorTypes.NONE,
      this.threeDService.materialFromFinishType(this._configuration.settings.wallFinishes[WallSide.ceiling].exterior),
      this._configuration.settings.sunkenFloor,
      this._configuration.settings.ventilationProfiles,
      this.plinths,
      this._configuration.settings.ceilingMoldings,
      wallFinishes,
      this._configuration.settings.doorSettings,
      this._configuration.settings.innerWalls,
      coolingCellCounterOverride
    );
  }

  setHideFrontWalls(hide: boolean) {
    if (hide === this._wallsHidden) {
      return;
    }

    this._wallsHidden = hide;
    this.threeDService.setWallsHidden(this._wallsHidden);
    this.hideFrontWallsChanged$.emit(this._wallsHidden);
  }

  get allowedFloorTypes(): FloorType[] {
    return Object.values(this._allowedFloorTypes);
  }

  get allowedFloorTypesRecord(): Record<FloorTypes, FloorType> {
    return this._allowedFloorTypes;
  }

  getSelectedDoorSettings(): DoorPosition {
    return this.threeDService.selectedDoor;
  }

  updateDoorSettings() {
    this.priceService.priceLoading();
    const threeDDoors = this.threeDService.getDoors();
    const newList = this._configuration.settings.doorSettings.filter(d => threeDDoors.some(td => d.id === td.id));
    threeDDoors.forEach(door => {
      const doorToUpdate = newList.find(d => d.id === door.id);
      if (doorToUpdate) {
        doorToUpdate.doorModel.direction = door.doorModel.direction;
        doorToUpdate.doorModel.openingHeight = door.doorModel.openingHeight;
        doorToUpdate.doorModel.openingWidth = door.doorModel.openingWidth;
        doorToUpdate.doorModel.panelHeight = door.doorModel.panelHeight;
        doorToUpdate.doorModel.panelWidth = door.doorModel.panelWidth;
        doorToUpdate.doorModel.type = door.doorModel.type;
        doorToUpdate.left = door.left;
        doorToUpdate.side = door.side;
        doorToUpdate.innerWallId = door.innerWallId;
      } else {
        door.frameExteriorFinish = this._configuration.settings.wallFinishes[door.side].exterior;
        door.frameInteriorFinish = this._configuration.settings.wallFinishes[door.side].interior;
        newList.push(door);
      }
    });

    this._configuration.settings.doorSettings = newList;
    this.configurationChanged$.emit(this._configuration);
    this.updatePrice$.emit(this.configuration);
    this.updateBlockingValues$.emit();
  }

  get ventilationProfiles() {
    return this.configuration.settings.ventilationProfiles;
  }

  set ventilationProfiles(value: boolean) {
    if (this.configuration.settings.ventilationProfiles !== value) {
      this.configuration.settings.ventilationProfiles = value;
      this.threeDService.setVentilationProfiles(value);

      this.configurationChanged$.emit(this._configuration);
      this.updatePrice$.emit(this.configuration);
    }
  }

  set sunkenFloor(value: boolean) {
    if (value === this._configuration.settings.sunkenFloor) {
      return;
    }
    this._configuration.settings.sunkenFloor = value;
    this.threeDService.setSunkenFloor(value);
    this.configurationChanged$.emit(this._configuration);
    this.updatePrice$.emit(this.configuration);
  }

  get sunkenFloor(): boolean {
    return this._configuration.settings.sunkenFloor;
  }

  set plinthsInside(value: boolean) {
    if (value === this._configuration.settings.plinthsInside) {
      return;
    }
    this._configuration.settings.plinthsInside = value;
    this.threeDService.setPlinthsOnSide(this.plinths);
    this.configurationChanged$.emit(this._configuration);
    this.updatePrice$.emit(this.configuration);
  }
  get plinthsInside(): boolean {
    return this._configuration.settings.plinthsInside;
  }

  set plinthsOutside(value: boolean) {
    if (value === this._configuration.settings.plinthsOutside) {
      return;
    }
    this._configuration.settings.plinthsOutside = value;
    this.threeDService.setPlinthsOnSide(this.plinths);

    this.configurationChanged$.emit(this._configuration);
    this.updatePrice$.emit(this.configuration);
  }
  get plinthsOutside(): boolean {
    return this._configuration.settings.plinthsOutside;
  }
  get plinths(): number {
    let value = 0;
    if (this.plinthsInside) {
      value += 1;
    }
    if (this.plinthsOutside) {
      value += 2;
    }
    return value;
  }
  set ceilingMoldings(value: boolean) {
    if (value === this._configuration.settings.ceilingMoldings) {
      return;
    }
    this._configuration.settings.ceilingMoldings = value;
    this.threeDService.setCeilingMoldings(value);

    this.configurationChanged$.emit(this._configuration);
    this.updatePrice$.emit(this.configuration);
  }
  get ceilingMoldings(): boolean {
    return this._configuration.settings.ceilingMoldings;
  }

  updateWallFinish(wallSide: WallSide, outside: boolean, finishType: FinishTypes, innerWallId?: string) {
    if (wallSide !== WallSide.inner) {
      const wallFinish = this._configuration.settings.wallFinishes[wallSide];
      const doorsToUpdate = this._configuration.settings.doorSettings.filter(d => d.side === wallSide);
      if (outside) {
        wallFinish.exterior = finishType;
        doorsToUpdate.forEach(d => (d.frameExteriorFinish = finishType));
      } else {
        wallFinish.interior = finishType;
        doorsToUpdate.forEach(d => (d.frameInteriorFinish = finishType));
      }
    } else {
      const doorsToUpdate = this._configuration.settings.doorSettings.filter(
        d => d.side === wallSide && d.innerWallId == innerWallId
      );
      const innerWall = this._configuration.settings.innerWalls.find(iw => iw.id === innerWallId);
      if (outside) {
        innerWall.exteriorFinish = finishType;
        doorsToUpdate.forEach(d => (d.frameExteriorFinish = finishType));
      } else {
        innerWall.interiorFinish = finishType;
        doorsToUpdate.forEach(d => (d.frameInteriorFinish = finishType));
      }
    }

    this.threeDService.updateWallFinish(wallSide, outside, finishType, innerWallId);

    this.configurationChanged$.emit(this._configuration);
    this.updatePrice$.emit(this.configuration);
  }

  updateSelectedDoorFinish(outside: boolean, finish: FinishTypes) {
    const doorToUpdate = this.configuration.settings.doorSettings.find(d => d.id === this.getSelectedDoorSettings().id);
    if (!doorToUpdate) {
      return;
    }

    if (outside) {
      doorToUpdate.setLeafExteriorFinish(finish);
    } else {
      doorToUpdate.setLeafInteriorFinish(finish);
    }

    this.threeDService.updateSelectedDoorFinish(outside, finish);
    this.configurationChanged$.emit(this._configuration);
    this.updatePrice$.emit(this.configuration);
  }

  get widthExternal(): number {
    let width = this.width + 2 * this.wallThickness;
    if (this.wallThickness <= 80) {
      width += 2 * this.roundedWallCorrectionThickness;
    }
    const innerWalls = this._configuration.settings.innerWalls.filter(
      iw => iw.placedOnSide === WallSide.north || iw.placedOnSide === WallSide.south
    );
    innerWalls.forEach(iw => {
      switch (iw.type) {
        case InnerWallType.threeway: {
          width += this.wallThickness;
          if (this.wallThickness <= 80) {
            width += 2 * this.roundedWallCorrectionThickness;
          }
          break;
        }
        default:
          break;
      }
    });
    return width;
  }
  get lengthExternal(): number {
    let length = this.length + 2 * this.wallThickness;
    if (this.wallThickness <= 80) {
      length += 2 * this.roundedWallCorrectionThickness;
    }
    const innerWalls = this._configuration.settings.innerWalls.filter(
      iw => iw.placedOnSide === WallSide.east || iw.placedOnSide === WallSide.west
    );
    innerWalls.forEach(iw => {
      switch (iw.type) {
        case InnerWallType.threeway: {
          length += this.wallThickness;
          if (this.wallThickness <= 80) {
            length += 2 * this.roundedWallCorrectionThickness;
          }
          break;
        }
        default:
          break;
      }
    });
    return length;
  }

  get heightExternal(): number {
    let height = this.height + this.wallThickness;
    if (this.wallThickness < 100) {
      height += 15;
    }
    if (this.floorType !== FloorTypes.NONE) {
      height += this.wallThickness;
      if (this.wallThickness < 100) {
        height += 15;
      }
    }
    if (this.ventilationProfiles) {
      height += 40;
    }
    return height;
  }

  updateInnerWalls() {
    this.configuration.settings.innerWalls = this.threeDService.getInnerWalls();
    this.updatePrice$.emit(this.configuration);
  }

  changeSelectedDoorHandle(doorHandleType: DoorHandleType) {
    const doorToUpdate = this.configuration.settings.doorSettings.find(d => d.id === this.getSelectedDoorSettings().id);
    if (!doorToUpdate) {
      return;
    }

    doorToUpdate.doorHandleType = doorHandleType;

    this.productClass = this.productClassificationService.GetDoorHandleClassification(doorHandleType);
    this.threeDService.changeSelectedDoorHandle(doorHandleType);
    this.configurationChanged$.emit(this._configuration);
    this.updatePrice$.emit(this.configuration);
  }

  changeSelectedDoorHinge(doorHingeType: HingeType) {
    const doorToUpdate = this.configuration.settings.doorSettings.find(d => d.id === this.getSelectedDoorSettings().id);
    if (!doorToUpdate) {
      return;
    }

    doorToUpdate.hingeType = doorHingeType;

    this.productClass = this.productClassificationService.GetHingeClassification(doorHingeType);
    this.threeDService.changeSelectedDoorHinge(doorHingeType);
    this.configurationChanged$.emit(this._configuration);
    this.updatePrice$.emit(this.configuration);
  }

  get canHavePlinths() {
    if (this.purpose !== Purpose.Cooling) {
      return false;
    }
    return this.wallThickness >= 100;
  }

  checkForInvalidPlinths() {
    if (this.plinthsInside && !this.canHavePlinths) {
      this.plinthsInside = false;
      this.notificationService.showNotification('NOTIFICATIONS.NO_PLINTHS', NotificationService.DEFAULT_TIMEOUT);
    }
  }
}
